import { Phone, Check, AlertCircle, Wrench, KeyRound, Truck, Clock } from "lucide-react";
import { COMPANY } from "../lib/constants";

const OPENING = [
  { label: "Porte claquée (jour)", price: "100 €" },
  { label: "Porte fermée à clé (jour)", price: "130 €" },
  { label: "Porte blindée claquée", price: "150 €" },
  { label: "Porte blindée claquée fermée à clé", price: "250 €" },
];

const CYLINDERS = [
  { label: "Remplacement cylindre standard (3 clés)", price: "90 à 150 €" },
  { label: "Remplacement cylindre sécurité (3 clés)", price: "140 à 260 €" },
  { label: "Changement serrure monopoint", price: "50 à 280 €" },
  { label: "Serrure multipoints", price: "280 à 650 €" },
  { label: "Blindage simple de porte", price: "350 à 900 €" },
];

const SURCHARGES = [
  { label: "De 18h30 à 21h", value: "+30 %" },
  { label: "Après 21h", value: "+50 %" },
  { label: "Dimanches et jours fériés", value: "+50 %" },
];

export default function PricingGrid({ cityName }) {
  return (
    <section
      data-testid="pricing-grid"
      className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24"
    >
      <div className="max-w-2xl mb-10">
        <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-teal-700 mb-3">
          <span className="h-px w-8 bg-teal-700" /> Grille tarifaire
        </div>
        <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tight text-slate-900 leading-tight">
          Tarifs <span className="text-teal-600">transparents</span>
          {cityName ? <> à {cityName}</> : null}.
        </h2>
        <p className="text-slate-600 mt-4 leading-relaxed">
          Prix TTC — TVA non applicable (article 293B du CGI). Devis annoncé et
          validé avant toute intervention. Aucune surprise.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Ouverture de porte */}
        <div className="lg:col-span-6 bg-white border border-slate-200 rounded-3xl p-7 md:p-8">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-11 h-11 rounded-2xl bg-teal-50 text-teal-700 flex items-center justify-center">
              <KeyRound className="w-5 h-5" />
            </div>
            <h3 className="font-display text-xl font-extrabold text-slate-900">
              Ouverture de porte
            </h3>
          </div>
          <ul className="divide-y divide-slate-100">
            {OPENING.map((it) => (
              <li
                key={it.label}
                className="flex items-center justify-between gap-4 py-3"
              >
                <span className="text-slate-700">{it.label}</span>
                <span className="font-display font-extrabold text-slate-900 whitespace-nowrap">
                  {it.price}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Cylindres / serrures */}
        <div className="lg:col-span-6 bg-slate-900 text-white rounded-3xl p-7 md:p-8 relative overflow-hidden">
          <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-teal-500/20 blur-3xl" />
          <div className="relative">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-11 h-11 rounded-2xl bg-white/10 text-teal-300 flex items-center justify-center">
                <Wrench className="w-5 h-5" />
              </div>
              <h3 className="font-display text-xl font-extrabold">
                Cylindres & serrures
              </h3>
            </div>
            <ul className="divide-y divide-white/10">
              {CYLINDERS.map((it) => (
                <li
                  key={it.label}
                  className="flex items-center justify-between gap-4 py-3"
                >
                  <span className="text-slate-200">{it.label}</span>
                  <span className="font-display font-extrabold text-white whitespace-nowrap">
                    {it.price}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Déplacement / main d'œuvre */}
        <div className="lg:col-span-6 bg-white border border-slate-200 rounded-3xl p-7 md:p-8">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-11 h-11 rounded-2xl bg-amber-50 text-amber-700 flex items-center justify-center">
              <Truck className="w-5 h-5" />
            </div>
            <h3 className="font-display text-xl font-extrabold text-slate-900">
              Déplacement & main-d'œuvre
            </h3>
          </div>
          <ul className="space-y-3">
            <li className="flex items-start gap-3">
              <Check className="w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0" />
              <span>
                <strong className="text-slate-900">Déplacement</strong> :
                forfait <strong>50 €</strong>
              </span>
            </li>
            <li className="flex items-start gap-3">
              <Check className="w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0" />
              <span>
                <strong className="text-slate-900">Main-d'œuvre</strong> :{" "}
                <strong>65 € à 85 €</strong> / heure{" "}
                <span className="text-slate-500 text-sm">(heure non divisible)</span>
              </span>
            </li>
            <li className="flex items-start gap-3">
              <Check className="w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0" />
              <span>
                Inclut <strong>machines et consommables</strong> utilisés
              </span>
            </li>
          </ul>
        </div>

        {/* Majorations horaires */}
        <div className="lg:col-span-6 bg-orange-50 border border-orange-200 rounded-3xl p-7 md:p-8">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-11 h-11 rounded-2xl bg-orange-600 text-white flex items-center justify-center">
              <Clock className="w-5 h-5" />
            </div>
            <h3 className="font-display text-xl font-extrabold text-slate-900">
              Majorations horaires
            </h3>
          </div>
          <ul className="space-y-3">
            {SURCHARGES.map((s) => (
              <li
                key={s.label}
                className="flex items-center justify-between gap-4 bg-white rounded-2xl px-4 py-3 border border-orange-100"
              >
                <span className="text-slate-700">{s.label}</span>
                <span className="font-display font-extrabold text-orange-700 whitespace-nowrap">
                  {s.value}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Mentions importantes */}
      <div
        data-testid="pricing-mentions"
        className="mt-8 rounded-3xl bg-slate-50 border border-slate-200 p-6 md:p-8"
      >
        <div className="flex items-start gap-3 mb-3">
          <AlertCircle className="w-5 h-5 text-slate-500 mt-0.5 flex-shrink-0" />
          <h3 className="font-display font-extrabold text-slate-900">
            Mentions importantes
          </h3>
        </div>
        <ul className="space-y-2 text-sm text-slate-600 leading-relaxed list-disc pl-5">
          <li>Toute prestation est payable intégralement à son exécution.</li>
          <li>
            Le matériel reste la propriété de l'entreprise jusqu'au paiement
            intégral.
          </li>
          <li>Les prix sont annoncés et validés avant intervention.</li>
          <li>Paiement immédiat si prestation(s) réalisée(s).</li>
        </ul>
      </div>

      {/* CTA — only shown on city pages */}
      {cityName && (
        <div className="mt-10 flex flex-col md:flex-row md:items-center justify-between gap-5 rounded-3xl bg-gradient-to-br from-teal-700 to-slate-900 text-white p-8 md:p-10">
          <div>
            <h3 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight">
              Un tarif personnalisé pour {cityName} ?
            </h3>
            <p className="text-teal-100 mt-1">
              Devis gratuit, sans engagement, en quelques minutes par téléphone.
            </p>
          </div>
          <a
            data-testid="pricing-cta-call"
            href={`tel:${COMPANY.phoneDigits}`}
            className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-full px-7 py-4 shadow-xl text-lg whitespace-nowrap"
          >
            <Phone className="w-5 h-5" /> {COMPANY.phone}
          </a>
        </div>
      )}
    </section>
  );
}
